import { useState, useEffect, useRef, useCallback } from 'react';

import { useLogger } from './useLogger';
import { createSensorEvent } from '../graphql/mutations';
import {
  getSensorCurrentState,
  getSensorEventsBySensor,
  getSensorEventsByHub,
  getSensorHistoricalState,
} from '../graphql/queries';
import {
  onCreateSensorEvent,
  onCreateSensorEventByHub,
  onCreateSensorEventBySensor,
} from '../graphql/subscriptions';
import { CreateSensorEventInput, SensorEvent, SensorType } from '../types';
import { API } from '../utils/amplifyBootstrap';

export const useSensorEvents = (hubId?: string, sensorId?: string) => {
  const logger = useLogger('useSensorEvents');
  const [sensorEvents, setSensorEvents] = useState<SensorEvent[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const subscriptionsRef = useRef<any[]>([]);

  const fetchSensorEventsBySensor = useCallback(async (sensorId: string, limit = 50) => {
    setLoading(true);
    setError(null);

    try {
      const response: any = await API.graphql({
        query: getSensorEventsBySensor,
        variables: { sensorId, limit },
      });
      const sensorEvents =
        response.data?.getSensorEventsBySensor?.items ||
        response.data?.getSensorEventsBySensor ||
        [];
      const parsedSensorEvents = sensorEvents.map(parseSensorEvent);

      setSensorEvents(parsedSensorEvents);
    } catch (error) {
      logger.error('Error fetching sensor events:', { error });
      setError(error as Error);
      setSensorEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSensorEventsByHub = useCallback(async (hubId: string, limit = 50) => {
    setLoading(true);
    setError(null);

    try {
      const response: any = await API.graphql({
        query: getSensorEventsByHub,
        variables: { hubId, limit },
      });
      const sensorEvents =
        response.data?.getSensorEventsByHub?.items || response.data?.getSensorEventsByHub || [];
      const parsedSensorEvents = sensorEvents.map(parseSensorEvent);

      setSensorEvents(parsedSensorEvents);
    } catch (error) {
      logger.error('Error fetching sensor events:', { error });
      setError(error as Error);
      setSensorEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const addSensorEvent = useCallback(async (input: CreateSensorEventInput) => {
    try {
      const response: any = await API.graphql({
        query: createSensorEvent,
        variables: { input },
      });
      return response.data.createSensorEvent;
    } catch (error) {
      logger.error('Error creating sensor event:', { error });
      throw error;
    }
  }, []);

  const fetchSensorCurrentState = useCallback(async (sensorId: string) => {
    try {
      const response: any = await API.graphql({
        query: getSensorCurrentState,
        variables: { sensorId },
      });
      return response.data.getSensorCurrentState;
    } catch (error) {
      logger.error('Error fetching sensor current state:', { error });
      throw error;
    }
  }, []);

  const fetchSensorHistoricalState = useCallback(
    async (sensorId: string, startTime: number, endTime: number, rollup: number) => {
      try {
        const response: any = await API.graphql({
          query: getSensorHistoricalState,
          variables: {
            sensorId,
            startTime,
            endTime,
            rollup,
          },
        });
        const sensorEvents =
          response.data?.getSensorHistoricalState?.items ||
          response.data?.getSensorHistoricalState ||
          [];
        const parsedSensorEvents = sensorEvents.map(parseSensorEvent);

        return parsedSensorEvents;
      } catch (error) {
        logger.error('Error fetching sensor historical state:', { error });
        throw error;
      }
    },
    [],
  );

  const clear = () => {
    setSensorEvents([]);
  };

  const fetchAndSubscribe = useCallback(() => {
    if (sensorId) {
      fetchSensorEventsBySensor(sensorId);

      logger.debug(`subscribing to sensor events for sensorId: ${sensorId}`);

      try {
        const createSubscription = (
          API.graphql({
            query: onCreateSensorEventBySensor,
            variables: { sensorId },
          }) as any
        ).subscribe({
          next: ({ data }: any) => {
            const newEvent = data.onSensorSensorEventCreated;
            if (newEvent) {
              setSensorEvents((prevEvents) => {
                const updatedEvents = [parseSensorEvent(newEvent), ...prevEvents];
                return updatedEvents.slice(0, 100);
              });
            }
          },
          error: (error: any) => logger.error('Create subscription error:', { error }),
        });

        subscriptionsRef.current = [createSubscription];
      } catch (error) {
        logger.error(`Error setting up subscriptions for sensor ID ${sensorId}`, { error });
      }

      return;
    }

    if (hubId) {
      fetchSensorEventsByHub(hubId);

      logger.debug(`subscribing to sensor events for hubId: ${hubId}`);

      try {
        const createSubscription = (
          API.graphql({
            query: onCreateSensorEventByHub,
            variables: { hubId },
          }) as any
        ).subscribe({
          next: ({ data }: any) => {
            const newEvent = data.onHubSensorEventCreated;
            if (newEvent) {
              setSensorEvents((prevEvents) => {
                const updatedEvents = [parseSensorEvent(newEvent), ...prevEvents];
                return updatedEvents.slice(0, 100);
              });
            }
          },
          error: (error: any) => logger.error('Create subscription error:', { error }),
        });

        subscriptionsRef.current = [createSubscription];
      } catch (error) {
        logger.error(`Error setting up subscriptions for hub ID ${hubId}:`, { error });
      }

      return;
    }

    logger.debug(`subscribing to all sensor events`);

    try {
      const createSubscription = (
        API.graphql({
          query: onCreateSensorEvent,
        }) as any
      ).subscribe({
        next: ({ data }: any) => {
          const newEvent = data.onSensorEventCreated;
          if (newEvent) {
            setSensorEvents((prevEvents) => {
              const updatedEvents = [parseSensorEvent(newEvent), ...prevEvents];
              return updatedEvents.slice(0, 100);
            });
          }
        },
        error: (error: any) => logger.error('Create subscription error:', { error }),
      });

      subscriptionsRef.current = [createSubscription];
    } catch (error) {
      logger.error('Error setting up subscriptions:', { error });
    }
  }, [hubId, sensorId, fetchSensorEventsBySensor, fetchSensorEventsByHub]);

  const unsubscribe = useCallback(() => {
    if (subscriptionsRef.current.length === 0) {
      return;
    }

    try {
      subscriptionsRef.current.forEach((subscription) => {
        if (subscription && typeof subscription.unsubscribe === 'function') {
          subscription.unsubscribe();
        }
      });
    } catch (error) {
      logger.error('Error cleaning up subscriptions:', { error });
    }
  }, []);

  const parsePressureValue = useCallback((value: any): number | undefined => {
    if (value && typeof value === 'number') {
      const maxTargetPressure = 1.0; // Should be taken from config, mocked for now
      const minStaticPressure = 0.1; // Should be taken from config, mocked for now
      const possibleChange = maxTargetPressure - minStaticPressure;
      if (value > possibleChange || value < 0) {
        return 0;
      }

      const change = possibleChange - value;
      const relativeChange = change / possibleChange;

      return Math.round(relativeChange * 100); // Convert to percentage
    }
  }, []);

  const convertTemperatureValue = useCallback((value: any): number | undefined => {
    const convertToFahrenheit = (value: number) => Math.round((value * 9) / 5 + 32); // (°C * 9/5) + 32

    if (!value) return undefined;
    if (typeof value === 'number') {
      return convertToFahrenheit(value);
    }
    if (typeof value === 'string') {
      const parsedValue = parseFloat(value);
      if (!isNaN(parsedValue)) {
        return convertToFahrenheit(parsedValue);
      }
    }
  }, []);

  type ExtendedSensorEvent = SensorEvent & {
    formattedValues?: Record<string, { value: number; unit: string; label: string }>;
  };

  const parseSensorEvent = useCallback((sensorEvent: SensorEvent): ExtendedSensorEvent => {
    const parsedSensorEvent: ExtendedSensorEvent = { ...sensorEvent };

    if (sensorEvent.metadata && typeof sensorEvent.metadata === 'string') {
      try {
        parsedSensorEvent.parsedMetadata = JSON.parse(sensorEvent.metadata);
      } catch (error) {
        logger.error(`Error parsing metadata for sensor event ${sensorEvent.id}:`, { error });
      }
    }

    if (sensorEvent.values && typeof sensorEvent.values === 'string') {
      try {
        const parsedValues = JSON.parse(sensorEvent.values);
        if (!parsedValues) {
          logger.error(`Error parsing values for sensor event ${sensorEvent.id}: values is empty`);
          parsedSensorEvent.values = sensorEvent.values;
          return parsedSensorEvent;
        }

        if (
          parsedSensorEvent.parsedMetadata?.measurementType &&
          parsedSensorEvent.parsedMetadata.measurementType === SensorType.HVAC_AIRHANDLER_MONITOR &&
          parsedValues.pressure
        ) {
          const parsedPressure = parsePressureValue(parsedValues.pressure);
          if (parsedPressure !== undefined) {
            parsedValues.percentPressure = parsedPressure;
          }
        }

        if (parsedValues.temperature) {
          const convertedTemperature = convertTemperatureValue(parsedValues.temperature);
          if (convertedTemperature !== undefined) {
            parsedValues.temperature = convertedTemperature;
          }
        }

        parsedSensorEvent.values = parsedValues;

        if (parsedSensorEvent.parsedMetadata?.measurementType === SensorType.POWER) {
          const formattedValues: Record<string, { value: number; unit: string; label: string }> =
            {};

          // Voltage transformers
          if (parsedValues.vt1 !== undefined) {
            formattedValues.vt1 = {
              value: parsedValues.vt1,
              unit: 'V',
              label: 'Voltage 1',
            };
          }
          if (parsedValues.vt2 !== undefined) {
            formattedValues.vt2 = {
              value: parsedValues.vt2,
              unit: 'V',
              label: 'Voltage 2',
            };
          }

          // Main current transformers
          if (parsedValues.main_ct1 !== undefined) {
            formattedValues.main_ct1 = {
              value: parsedValues.main_ct1,
              unit: 'A',
              label: 'Main CT 1',
            };
          }
          if (parsedValues.main_ct2 !== undefined) {
            formattedValues.main_ct2 = {
              value: parsedValues.main_ct2,
              unit: 'A',
              label: 'Main CT 2',
            };
          }

          // Branch current transformers
          for (let i = 1; i <= 14; i++) {
            const key = `branch_ct${i}`;
            if (parsedValues[key] !== undefined) {
              formattedValues[key] = {
                value: parsedValues[key],
                unit: 'A',
                label: `Branch CT ${i}`,
              };
            }
          }

          parsedSensorEvent.formattedValues = formattedValues;
        }
      } catch (error) {
        logger.error(`Error parsing values for sensor event ${sensorEvent.id}:`, { error });
        parsedSensorEvent.values = sensorEvent.values;
      }
    }

    return parsedSensorEvent;
  }, []);

  useEffect(() => {
    unsubscribe();
    fetchAndSubscribe();

    return () => {
      unsubscribe();
    };
  }, [hubId, sensorId]);

  return {
    sensorEvents,
    loading,
    error,
    fetchSensorEventsBySensor,
    fetchSensorEventsByHub,
    fetchSensorCurrentState,
    fetchSensorHistoricalState,
    addSensorEvent,
    clear,
  };
};
